import { AlertTriangle, Home, RotateCcw } from 'lucide-react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';

import { WA_LINKS } from '@/lib/whatsapp';

import { Container } from '../common/Container';
import { BruceHeadIcon, WhatsAppIcon } from '../common/icons';
import { Button } from '../ui/button';

/**
 * Last line of defence for a render or loader failure.
 *
 * Rendered outside `RootLayout` — the header may be what crashed — so it brings
 * its own shell and always offers a route back plus a human to talk to.
 */
export function RouteError() {
  const error = useRouteError();

  const is404 = isRouteErrorResponse(error) && error.status === 404;
  const detail = import.meta.env.DEV && error instanceof Error ? error.message : undefined;

  return (
    <div className="flex min-h-screen items-center bg-cream-100">
      <Container size="sm" className="flex flex-col items-center gap-6 py-20 text-center">
        {is404 ? (
          <BruceHeadIcon className="size-24 text-sand-300" />
        ) : (
          <span className="grid size-20 place-items-center rounded-3xl bg-orange-100">
            <AlertTriangle className="size-10 text-orange-600" aria-hidden="true" />
          </span>
        )}

        <div>
          <h1 className="text-2xl font-extrabold text-brown-900 lg:text-3xl">
            {is404 ? 'Página não encontrada' : 'Algo saiu do lugar por aqui'}
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-brown-700">
            {is404
              ? 'O endereço que você acessou não existe mais.'
              : 'Tivemos um problema ao carregar esta página. Tente recarregar — e, se continuar, chame a gente no WhatsApp que resolvemos seu pedido por lá.'}
          </p>

          {detail && (
            <pre className="mt-4 overflow-x-auto rounded-xl border border-sand-300 bg-white p-3 text-left text-xs text-brown-700">
              {detail}
            </pre>
          )}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button size="lg" onClick={() => window.location.reload()}>
            <RotateCcw aria-hidden="true" />
            Recarregar
          </Button>

          <Button asChild variant="outline" size="lg">
            <Link to="/">
              <Home aria-hidden="true" />
              Ir para o início
            </Link>
          </Button>
        </div>

        <a
          href={WA_LINKS.general}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm font-bold text-whats-600 transition-colors hover:text-whats-700"
        >
          <WhatsAppIcon className="size-4" />
          Falar com a BrucePets
        </a>
      </Container>
    </div>
  );
}
